import { Form, Select } from 'antd'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { isValidDate } from '../../../../utils/requestHelper'
import { getFormattedDate } from '../../../../utils/getLocalFormattedDate'
import { buildGlobalMasterLabelMap, buildMasterOptionLabelMap, getLocalizedMasterCellText, resolveMasterValueLabel } from '../../../../utils/masterHelper'

const formatOptionLabel = (val) => {
    if (typeof val === 'string' && isValidDate(val)) {
        return getFormattedDate(val)
    }
    return val
}

const SelectForm = (props) => {
    const {
        id,
        values,
        options: incomingOptions,
        langOptions,
        multiSelect,
        handleChange,
        documentId,
        disabled = false,
        tableLabel = "",
        parentTable,
        mappedColumn,
        allColumns = [],
        lng
    } = props
    let currentValue = values?.[id]
    const [readOnlyValue, setReadOnlyValue] = useState("")
    const { i18n } = useTranslation()
    const contentLang = lng || i18n.language
    const { userInfo } = useSelector(state => state.user)
    const { masterList = [], masterDataCache = {} } = useSelector(state => state.masterData)
    const isMasterConnected = Boolean(parentTable && mappedColumn)
    const isValidSelectionValue = (val) => {
        if (val === undefined || val === null) return false
        if (typeof val === "string" && val.trim() === "") return false
        return true
    }
    const hasMultiValue = Array.isArray(currentValue) && currentValue.filter(isValidSelectionValue).length > 1

    const getMasterRows = useCallback(() => {
        if (!parentTable) {
            return []
        }
        if (Array.isArray(masterDataCache?.[parentTable])) {
            return masterDataCache[parentTable]
        }
        const selectedMaster = (Array.isArray(masterList) ? masterList : []).find((master) => master?.dataId === parentTable)
        return Array.isArray(selectedMaster?.data) ? selectedMaster.data : []
    }, [parentTable, masterDataCache, masterList])

    // The mapped column can itself point at another master, in which case the row holds the
    // referenced row's uuid rather than display text. Resolve it the same way the master data
    // table does; a value that resolves to nothing is returned untouched.
    const resolveMasterLabel = useCallback((rawValue, row) => {
        if (!isMasterConnected) {
            return rawValue
        }
        const currentMasterList = Array.isArray(masterList) ? masterList : []
        const masterDef = currentMasterList.find((master) => master?.dataId === parentTable)
        const columnField = masterDef?.fields?.find((field) => field?.label === mappedColumn)
        if (row) {
            return getLocalizedMasterCellText(row, mappedColumn, columnField, masterDef, currentMasterList, contentLang)
        }
        return resolveMasterValueLabel(
            rawValue,
            buildMasterOptionLabelMap(columnField, currentMasterList, contentLang),
            buildGlobalMasterLabelMap(currentMasterList, contentLang)
        )
    }, [isMasterConnected, parentTable, mappedColumn, masterList, contentLang])

    // A column that points at another master is answered with the id its cell holds,
    // so the same choice carries the same id here, on a plain master dropdown and on
    // the referenced master's own field.
    const isReferenceColumn = useMemo(() => {
        if (!isMasterConnected) return false
        const masterDef = (Array.isArray(masterList) ? masterList : []).find((master) => master?.dataId === parentTable)
        return !!masterDef?.fields?.find((field) => field?.label === mappedColumn)?.parentTable
    }, [isMasterConnected, masterList, parentTable, mappedColumn])

    const getFilteredMasterOptions = useCallback((rows = []) => {
        if (!mappedColumn) {
            return { options: [], hasSiblingSelection: false }
        }
        const currentColumns = Array.isArray(allColumns) ? allColumns : []
        const currentColumnIndex = currentColumns.findIndex((column) => column?.id === id)
        const siblingColumns = currentColumns.filter((column, index) =>
            column?.id !== id
            && column?.inputType === 'dropdown'
            && (currentColumnIndex < 0 || index < currentColumnIndex)
            && values?.[column.id] !== undefined
            && values?.[column.id] !== null
            && values?.[column.id] !== ""
        )

        const isRowMatchingSiblingSelection = (row, sibling) => {
            const siblingSelection = values?.[sibling.id]
            if (!siblingSelection || !row) {
                return true
            }

            if (sibling?.parentTable && sibling.parentTable === parentTable && row.uuid && String(row.uuid) === String(siblingSelection)) {
                return true
            }

            const siblingOptions = Array.isArray(sibling?.options) ? sibling.options : []
            const selectedOption = siblingOptions.find((opt) => String(opt?.value) === String(siblingSelection))
            const selectedLabel = selectedOption?.label

            if (selectedLabel !== undefined && selectedLabel !== null && selectedLabel !== "") {
                if (sibling?.mappedColumn && String(row?.[sibling.mappedColumn] ?? "") === String(selectedLabel)) {
                    return true
                }

                if (String(row?.Name ?? "") === String(selectedLabel)) {
                    return true
                }
            }

            if (sibling?.mappedColumn && String(row?.[sibling.mappedColumn] ?? "") === String(siblingSelection)) {
                return true
            }

            return String(row?.Name ?? "") === String(siblingSelection)
        }

        const filteredRows = rows.filter((row) => {
            if (!row || row[mappedColumn] === undefined || row[mappedColumn] === null || row[mappedColumn] === "") {
                return false
            }
            return siblingColumns.every((sibling) => isRowMatchingSiblingSelection(row, sibling))
        })

        const uniqueOptionsMap = new Map()
        filteredRows.forEach((row) => {
            const val = row[mappedColumn]
            if (Array.isArray(val)) {
                val.forEach(item => {
                    if (item !== undefined && item !== null && item !== "") {
                        const optionKey = String(item).trim().toLowerCase()
                        if (!uniqueOptionsMap.has(optionKey)) {
                            uniqueOptionsMap.set(optionKey, { label: String(formatOptionLabel(resolveMasterLabel(item, row))), value: String(item) })
                        }
                    }
                })
            } else if (val !== undefined && val !== null && val !== "") {
                const optionLabel = formatOptionLabel(resolveMasterLabel(val, row))
                const optionValue = isReferenceColumn ? val : (row.uuid || val)
                const optionKey = String(val).trim().toLowerCase()
                if (!uniqueOptionsMap.has(optionKey)) {
                    uniqueOptionsMap.set(optionKey, { label: optionLabel, value: optionValue })
                }
            }
        })

        return {
            options: Array.from(uniqueOptionsMap.values()),
            hasSiblingSelection: siblingColumns.length > 0
        }
    }, [mappedColumn, allColumns, id, values, parentTable, isReferenceColumn, resolveMasterLabel])

    const { options, hasSiblingSelection } = useMemo(() => {
        if (parentTable && mappedColumn) {
            const masterRows = getMasterRows()
            const { options: mappedOptions, hasSiblingSelection: siblingSelected } = getFilteredMasterOptions(masterRows)
            const configuredOptions = Array.isArray(incomingOptions) ? incomingOptions : []
            const resolved = siblingSelected
                ? mappedOptions
                : (mappedOptions.length > 0 ? mappedOptions : configuredOptions)
            return { options: resolved, hasSiblingSelection: siblingSelected }
        }

        let nextOptions = Array.isArray(incomingOptions) ? incomingOptions : []
        if (langOptions && userInfo.defaultLanguage) {
            const reqOptions = langOptions[userInfo.defaultLanguage]
            nextOptions = Array.isArray(reqOptions) ? reqOptions : []
        }
        return { options: nextOptions, hasSiblingSelection: false }
    }, [parentTable, mappedColumn, getMasterRows, getFilteredMasterOptions, incomingOptions, langOptions, userInfo.defaultLanguage])

    const autoMultipleByOptions = isMasterConnected && hasSiblingSelection && Array.isArray(options) && options.length > 1
    const isMultipleMode = isMasterConnected
        ? Boolean(autoMultipleByOptions || hasMultiValue)
        : Boolean(multiSelect || hasMultiValue)
    const normalizedValue = useMemo(() => (
        isMultipleMode
            ? (Array.isArray(currentValue)
                ? currentValue.filter(isValidSelectionValue)
                : (isValidSelectionValue(currentValue) ? [currentValue] : []))
            : (Array.isArray(currentValue)
                ? currentValue.find(isValidSelectionValue)
                : (isValidSelectionValue(currentValue) ? currentValue : undefined))
    ), [isMultipleMode, currentValue])

    const optionsWithSelection = useMemo(() => {
        const baseOptions = Array.isArray(options) ? options : []
        if (!isMasterConnected || !mappedColumn) {
            return baseOptions
        }

        const selectedValues = Array.isArray(currentValue) ? currentValue : [currentValue]
        const validSelectedValues = selectedValues.filter(isValidSelectionValue)
        if (validSelectedValues.length === 0) {
            return baseOptions
        }

        const nextOptions = [...baseOptions]
        const existingValues = new Set(baseOptions.map((opt) => String(opt?.value)))
        const masterRows = getMasterRows()

        validSelectedValues.forEach((selectedVal) => {
            const selectedKey = String(selectedVal)
            if (existingValues.has(selectedKey)) {
                return
            }

            const matchedRow = (Array.isArray(masterRows) ? masterRows : []).find((row) => {
                if (!row) return false
                return String(row?.uuid ?? "") === selectedKey || String(row?.[mappedColumn] ?? "") === selectedKey
            })

            if (!matchedRow) {
                return
            }

            const fallbackLabel = matchedRow?.[mappedColumn]
            // Offered under the id that is actually stored - the row uuid for an answer
            // saved before a reference column was keyed by the row it points at, the
            // referenced id after - so either one still reads as the choice it was.
            const fallbackValue = selectedVal
            if (!isValidSelectionValue(fallbackValue)) {
                return
            }

            const fallbackValueKey = String(fallbackValue)
            if (!existingValues.has(fallbackValueKey)) {
                nextOptions.push({
                    label: isValidSelectionValue(fallbackLabel) ? String(formatOptionLabel(resolveMasterLabel(fallbackLabel, matchedRow))) : String(formatOptionLabel(selectedKey)),
                    value: fallbackValue
                })
                existingValues.add(fallbackValueKey)
            }
        })

        return nextOptions
    }, [options, isMasterConnected, mappedColumn, currentValue, getMasterRows, resolveMasterLabel])

    useEffect(() => {
        if (!disabled) {
            return
        }
        const lookupOptions = optionsWithSelection
        if (Array.isArray(currentValue)) {
            const selectedLabels = currentValue.map(val => {
                const opt = lookupOptions.find(o => o.value === val);
                return opt ? opt.label : formatOptionLabel(val);
            });
            setReadOnlyValue(selectedLabels.join(", "));
        } else {
            const actualValue = lookupOptions.find(opt => opt.value === currentValue)
            setReadOnlyValue(actualValue?.label || formatOptionLabel(currentValue) || "")
        }
    }, [disabled, currentValue, optionsWithSelection])

    useEffect(() => {
        if (disabled || !handleChange) {
            return
        }

        if (isMasterConnected) {
            if (hasSiblingSelection) {
                if (isMultipleMode) {
                    const allOptionValues = options
                        .map((opt) => opt?.value)
                        .filter(isValidSelectionValue)
                    const selectedValues = Array.isArray(normalizedValue)
                        ? normalizedValue.filter(isValidSelectionValue)
                        : []
                    const allOptionValueSet = new Set(allOptionValues.map((val) => String(val)))

                    const shouldPopulateAll =
                        allOptionValues.length > 0 && (
                            selectedValues.length !== allOptionValues.length
                            || selectedValues.some((val) => !allOptionValueSet.has(String(val)))
                        )

                    if (shouldPopulateAll) {
                        handleChange(id, allOptionValues, documentId)
                        return
                    }

                    if (allOptionValues.length === 0 && selectedValues.length > 0) {
                        handleChange(id, [], documentId)
                        return
                    }
                } else {
                    const singleCurrentValue = Array.isArray(currentValue) ? currentValue[0] : currentValue
                    const valueExists = options.some((opt) => opt.value === singleCurrentValue)
                    if (!valueExists) {
                        if (options.length > 0) {
                            handleChange(id, options[0]?.value, documentId)
                        } else if (currentValue !== undefined && currentValue !== null && currentValue !== "") {
                            handleChange(id, undefined, documentId)
                        }
                        return
                    }
                }
            }
        }

        if (isMultipleMode && Array.isArray(normalizedValue)) {
            const valuesToValidate = normalizedValue
            const validValues = valuesToValidate.filter((val) => optionsWithSelection.some((opt) => opt.value === val))
            if (validValues.length !== valuesToValidate.length) {
                handleChange(id, validValues, documentId)
            }
            return
        }

        if (!isMultipleMode && Array.isArray(currentValue)) {
            const nextSingleValue = currentValue.length > 0 ? currentValue[0] : undefined
            handleChange(id, nextSingleValue, documentId)
            return
        }

        if (currentValue !== undefined && currentValue !== null && currentValue !== "") {
            const singleCurrentValue = Array.isArray(currentValue) ? currentValue[0] : currentValue
            const valueExists = optionsWithSelection.some((opt) => opt.value === singleCurrentValue)
            if (!valueExists) {
                handleChange(id, undefined, documentId)
            }
        }
    }, [options, optionsWithSelection, currentValue, normalizedValue, disabled, isMultipleMode, hasSiblingSelection, isMasterConnected, handleChange, id, documentId])

    return (
        <>
            {
                disabled ? <span>{readOnlyValue}</span>
                    :
                    <Form.Item label="" preserve={false} style={{ marginBottom: 0 }}>
                        <Select {...(isMultipleMode && { mode: "multiple" })} disabled={disabled} className='inline-select' value={normalizedValue} options={optionsWithSelection}
                            onChange={(val) => handleChange && handleChange(id, val, documentId)}
                            showSearch
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                            style={{ fontSize: "13px", fontWeight: "400" }} />
                    </Form.Item>
            }
        </>
    )
}

export default SelectForm
